import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import Pagination from "./Pagination";
import { PLAN_OPTIONS } from "./constants";
import { Search, Ban, RotateCcw, Trash2, Gift, MessageCircle } from "lucide-react";

function formatTelefoneDisplay(tel) {
  if (!tel) return "";
  const d = String(tel).replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return tel;
}

export default function UsersPanel({ users, search, onSearchChange, onSearchSubmit, onPage, onChangePlan, onToggleBan, onDelete, onGrant }) {
  return (
    <div className="glass-card overflow-hidden">
      <div className="section-accent p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-100">Controle de Usuários</h2>
          <p className="text-xs text-slate-500 mt-0.5">Busca, paginação, banimento, acesso cortesia/teste e exclusão permanente.</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSearchSubmit()}
            placeholder="Buscar por nome, e-mail ou WhatsApp..."
            data-testid="admin-user-search"
            className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 pl-9 h-9 text-sm" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm table-zebra">
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Usuário</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">WhatsApp</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Plano</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Chips</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Status</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {users.items.map((u) => {
              const rawTel = u.telefone || "";
              const telE164 = u.telefone_e164 || (rawTel ? `55${rawTel.replace(/\D/g, "")}` : "");
              return (
                <tr key={u.id} data-testid={`admin-user-table-row-${u.id}`}>
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-slate-200">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.email}</p>
                    {u.access_granted_by_admin && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 mt-1 inline-block">
                        ACESSO CORTESIA
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {rawTel ? (
                      <a
                        href={`https://wa.me/${telE164}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Abrir conversa no WhatsApp"
                        data-testid={`admin-user-whatsapp-${u.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/25 transition-colors text-xs font-mono group"
                      >
                        <MessageCircle className="w-3.5 h-3.5 text-emerald-400 group-hover:scale-110 transition-transform" />
                        <span>{formatTelefoneDisplay(rawTel)}</span>
                      </a>
                    ) : (
                      <span className="text-xs text-slate-500 italic">Sem WhatsApp</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {u.role === "admin" ? (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30 uppercase tracking-wider">Admin</span>
                    ) : (
                      <Select value={u.plan || ""} onValueChange={(v) => onChangePlan(u.id, v)}>
                        <SelectTrigger data-testid={`admin-btn-change-plan-${u.id}`} className="bg-slate-800/60 border-slate-700 text-slate-100 h-8 w-40 text-xs">
                          <SelectValue placeholder="Sem plano" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                          {PLAN_OPTIONS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-5 py-3.5 font-mono text-slate-300">{u.chips_count}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider border ${
                      u.status === "ativo" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-red-500/15 text-red-300 border-red-500/30"
                    }`}>
                      {u.status === "ativo" ? "Ativo" : "Banido"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    {u.role !== "admin" && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => onGrant(u)}
                          data-testid={`admin-btn-grant-${u.id}`}
                          className="h-8 text-xs border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10">
                          <Gift className="w-3.5 h-3.5 mr-1" /> Cortesia
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onToggleBan(u)}
                          data-testid={`admin-btn-ban-user-${u.id}`}
                          className={`h-8 text-xs ${u.status === "banido" ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" : "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"}`}>
                          {u.status === "banido" ? (<><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reativar</>) : (<><Ban className="w-3.5 h-3.5 mr-1" /> Banir</>)}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onDelete(u)}
                          data-testid={`admin-btn-delete-user-${u.id}`}
                          className="h-8 text-xs border-red-500/40 text-red-300 hover:bg-red-500/10">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination page={users.page} pages={users.pages} total={users.total} onPage={onPage} testid="admin-users-pagination" />
    </div>
  );
}
